import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  createRef,
  RefObject
} from 'react';
import { IPhrase, IGameRoundAnswer, useIsMounted } from 'common/modules/ReactShared';
import { useFlashcardHint } from './useFlashcardHint';

type tStage = 'start' | 'inGame' | 'finished';

type tItemType = 'prev' | 'current' | 'next';

interface IDisplayItem {
  itemType: tItemType;
  word: IPhrase;
  hasImage: string | null;
}

type tCardRef = {
  enterAnimation(): Promise<void>;
  hideContent(): void;
  nextAnimation(): Promise<void>;
  removeAnimation(): Promise<void>;
  initialAnimation(): Promise<void>;
  mountedAnimation(): void;
  prevAnimation(): Promise<void>;
} | null;

type tFlashCardViewModelArgs = {
  words: IPhrase[];
  onRoundComplete: (answer: IGameRoundAnswer) => void;
  preferences: {
    trainingHints: boolean;
  };
};

type tFlashCardViewModel = {
  stage: tStage;
  handleStart: () => void;
  canGoPrev: boolean;
  handleClickPrev: () => Promise<void>;
  canGoNext: boolean;
  handleClickNext: () => Promise<void>;
  displayItems: IDisplayItem[];
  activeItem?: IPhrase;
  handleSwipeNext: () => void;
  hintVisible: boolean;
  shaking: boolean;
  round: number;
  refs: Record<string, React.RefObject<tCardRef>>;
  increaseRound: () => void;
  decreaseRound: () => void;
  handleSkipAllRounds: () => void;
};

const ITEM_TYPES: tItemType[] = ['prev', 'current', 'next'];

const useFlashCard = ({
  words,
  onRoundComplete,
  preferences
}: tFlashCardViewModelArgs): tFlashCardViewModel => {
  const { checkIfMounted } = useIsMounted();
  const [stage, setStage] = useState<tStage>('start');
  const [round, setRound] = useState(0);
  const [hintVisible, updateLastInteractionTime, shaking] = useFlashcardHint(
    round === 0 && stage === 'inGame' && preferences.trainingHints
  );
  const clickAnimationCompletedRef = useRef(true);
  const refs = useMemo(
    () =>
      ITEM_TYPES.reduce((acc: Record<string, RefObject<tCardRef>>, item) => {
        // eslint-disable-next-line no-param-reassign
        acc[item] = createRef();
        return acc;
      }, {}),
    []
  );

  const itemsToDisplay = useMemo(() => {
    if (round === 0) {
      return [...words].splice(0, 2);
    }
    return [...words].splice(round - 1, 3);
  }, [words, round]);

  const displayItems: IDisplayItem[] = useMemo(() => {
    if (round === 0) {
      return itemsToDisplay.map((item, index) => ({
        itemType: index === 0 ? 'current' : 'next',
        word: item,
        hasImage: item.imageURL || item.animationURL
      }));
    }

    if (round === words.length - 1) {
      return itemsToDisplay.map((item, index) => ({
        itemType: index === 0 ? 'prev' : 'current',
        word: item,
        hasImage: item.imageURL || item.animationURL
      }));
    }

    return itemsToDisplay.map((item, index) => ({
      itemType: ITEM_TYPES[index],
      word: item,
      hasImage: item.imageURL || item.animationURL
    }));
  }, [itemsToDisplay, round, words.length]);

  const activeItem = useMemo<IPhrase | undefined>(() => words[round], [round, words]);

  const canGoNext = round < words.length;
  const canGoPrev = round > 0;

  const increaseRound = useCallback(() => {
    if (canGoNext) setRound(round + 1);
  }, [canGoNext, round]);

  const decreaseRound = useCallback(() => {
    if (canGoPrev) setRound(round - 1);
  }, [canGoPrev, round]);

  const handleStart = useCallback(() => {
    setStage('inGame');
  }, []);
  const handleSwipeNext = useCallback(async () => {
    if (canGoNext && activeItem) {
      updateLastInteractionTime();
      onRoundComplete({
        id: activeItem.id,
        word: activeItem.body,
        solved: true
      });
      if (round === words.length - 1) {
        setStage('finished');
      } else {
        await refs.next.current?.enterAnimation();
        refs.current.current?.hideContent();
        if (checkIfMounted()) {
          increaseRound();
        }
      }
    }
  }, [
    activeItem,
    canGoNext,
    checkIfMounted,
    increaseRound,
    onRoundComplete,
    refs,
    round,
    updateLastInteractionTime,
    words.length
  ]);

  const handleClickNext = useCallback(async () => {
    updateLastInteractionTime();
    if (clickAnimationCompletedRef.current) {
      clickAnimationCompletedRef.current = false;
      await refs.current.current?.nextAnimation();
      await refs.current.current?.removeAnimation();
      await handleSwipeNext();
      clickAnimationCompletedRef.current = true;
    }
  }, [handleSwipeNext, refs, updateLastInteractionTime]);

  const handleClickPrev = useCallback(async () => {
    updateLastInteractionTime();
    if (clickAnimationCompletedRef.current) {
      clickAnimationCompletedRef.current = false;
      await refs.next.current?.initialAnimation();
      refs.current.current?.mountedAnimation();
      await refs.prev.current?.prevAnimation();
      clickAnimationCompletedRef.current = true;
      decreaseRound();
    }
  }, [decreaseRound, refs, updateLastInteractionTime]);

  const handleSkipAllRounds = useCallback(() => {
    setStage('finished');
  }, [setStage]);

  return {
    stage,
    handleStart,
    canGoPrev,
    handleClickPrev,
    canGoNext,
    handleClickNext,
    displayItems,
    activeItem,
    handleSwipeNext,
    hintVisible,
    shaking,
    round,
    refs,
    decreaseRound,
    increaseRound,
    handleSkipAllRounds
  };
};

export { useFlashCard };
export type {
  tFlashCardViewModelArgs,
  tFlashCardViewModel,
  tCardRef,
  tStage,
  IDisplayItem
};
